import { Link } from "react-router-dom";
import { Users } from "lucide-react";
import { Reveal, Eyebrow, PageHero, PrimaryLink, OutlineLink, ArrowLink } from "@/components/shared";
import { programs, beyondPrograms, IMAGES } from "@/data/content";
import { useLang } from "@/i18n";

const OurWork = () => {
  const { lang, t } = useLang();
  const kn = lang === "kn";

  return (
    <>
      <PageHero
        testId="our-work-hero"
        eyebrow={t("ow.heroEyebrow")}
        title={t("ow.heroTitle")}
        intro={t("ow.heroIntro")}
      />

      <section className="bg-ivory" data-testid="our-work-intro-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Eyebrow>{t("ow.introEyebrow")}</Eyebrow>
            <h2 className="text-[32px] sm:text-4xl lg:text-5xl font-semibold text-charcoal">{t("ow.introHeading")}</h2>
            <p className="mt-5 text-bodytext/80 leading-relaxed">{t("ow.introBody")}</p>
            <p className="mt-4 text-bodytext/80 leading-relaxed" data-testid="our-work-lykit-paragraph">{t("ow.introLykit")}</p>
            <Link to="/maha-karuna" className="link-arrow mt-4 text-[14px]" data-testid="our-work-karuna-link">
              {kn ? "ಮಹಾಕರುಣ 2026 ಕಥನ" : "The Maha Karuna 2026 Story"}
            </Link>
            <blockquote className="mt-6 font-serif italic text-xl lg:text-2xl text-charcoal/90 border-l-2 border-saffron pl-6" data-testid="our-work-highlight-quote">
              {kn
                ? "“ನೀವು ಒಂಟಿಯಲ್ಲ — ಇದು ನಿಮ್ಮ ಮನೆ.”"
                : "“You are not alone — this is your home.”"}
            </blockquote>
            <div className="mt-7">
              <PrimaryLink to="/donate" testId="our-work-intro-donate-button">{t("ow.introCta")}</PrimaryLink>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="img-zoom !rounded-2xl aspect-[4/3]">
              <img src={IMAGES.praying} alt="IBMCT monks in prayer during a community gathering" className="w-full h-full object-cover" data-testid="our-work-intro-image" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-sandbg" data-testid="our-work-programs-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <Reveal>
            <Eyebrow>{t("ow.flagshipEyebrow")}</Eyebrow>
            <h2 className="text-[32px] sm:text-4xl lg:text-5xl font-semibold text-charcoal max-w-3xl">{t("work.heading")}</h2>
            <p className="mt-4 font-serif italic text-lg text-bodytext/70 max-w-2xl">{t("work.subhead")}</p>
          </Reveal>

          <div className="mt-16 lg:mt-24 space-y-24 lg:space-y-32">
            {programs.map((p, i) => {
              const imageLeft = i % 2 === 0;
              const aspects = ["aspect-[4/5]", "aspect-[3/2]", "aspect-[4/3]"];
              const spans = ["lg:col-span-7", "lg:col-span-7", "lg:col-span-6"];
              const textSpans = ["lg:col-span-5", "lg:col-span-5", "lg:col-span-6"];
              const aspect = aspects[i] || "aspect-[4/3]";
              const imgCol = spans[i] || "lg:col-span-7";
              const txtCol = textSpans[i] || "lg:col-span-5";

              return (
                <Reveal key={p.id} delay={80} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-testid={`our-work-editorial-${p.id}`}>
                  {/* Image column */}
                  <div className={`${imgCol} ${imageLeft ? "lg:order-1" : "lg:order-2"}`}>
                    <div className="relative">
                      <Link
                        to={`/our-work/${p.id}`}
                        className="block img-zoom !rounded-3xl overflow-hidden shadow-[0_24px_60px_-30px_rgba(23,18,13,0.55)] no-underline"
                        data-testid={`editorial-image-${p.id}`}
                      >
                        <div className={`${aspect} w-full`}>
                          <img
                            src={p.scene}
                            alt={`${p.name} — architectural rendering`}
                            className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]"
                            loading="lazy"
                          />
                        </div>
                      </Link>
                      {/* Emblem seal — overlaps image corner */}
                      <span
                        className="hidden md:flex absolute -bottom-8 -right-6 lg:-bottom-10 lg:-right-8 w-28 h-28 lg:w-36 lg:h-36 rounded-full items-center justify-center border-[3px] border-cream shadow-[0_16px_40px_-16px_rgba(23,18,13,0.55)] p-3"
                        style={{ backgroundColor: p.emblemBg }}
                        aria-hidden="true"
                      >
                        <img src={p.emblem} alt="" className="max-w-full max-h-full object-contain" />
                      </span>
                    </div>
                  </div>

                  {/* Text column */}
                  <div className={`${txtCol} ${imageLeft ? "lg:order-2" : "lg:order-1"}`}>
                    <div className="flex items-baseline gap-5">
                      <span className="font-serif text-[64px] lg:text-[80px] leading-none font-semibold text-saffron/25" aria-hidden="true">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-sans text-[11px] font-extrabold uppercase tracking-[0.24em] text-saffron">
                        {t("work.tag")}
                      </span>
                    </div>
                    <h3 className="mt-4 font-serif text-[34px] sm:text-[40px] lg:text-[46px] font-semibold leading-[1.05] text-charcoal">
                      {kn ? p.knName : p.name}
                    </h3>
                    <p className="mt-3 font-serif italic text-[19px] lg:text-[21px] text-charcoal/75 leading-snug">
                      {kn ? p.kn.tagline : p.tagline}
                    </p>
                    <div className="mt-6 w-14 h-px bg-saffron" aria-hidden="true" />
                    <p className="mt-6 text-[16px] lg:text-[17px] leading-relaxed text-bodytext/85 max-w-xl">
                      {kn ? p.kn.desc : p.desc}
                    </p>
                    <Link
                      to={`/our-work/${p.id}`}
                      className="mt-8 inline-flex items-center gap-3 font-sans font-semibold text-[14.5px] tracking-[0.02em] text-saffrondeep no-underline group"
                      data-testid={`editorial-cta-${p.id}`}
                    >
                      <span className="uppercase tracking-[0.2em] text-[12px] text-saffron">Explore</span>
                      <span className="font-serif italic text-charcoal group-hover:text-saffron transition-colors">
                        {kn ? p.knName : p.name} →
                      </span>
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-ivory" data-testid="community-services-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <Eyebrow>{t("ow.communityEyebrow")}</Eyebrow>
              <h2 className="text-[32px] sm:text-4xl font-semibold text-charcoal">{t("ow.communityHeading")}</h2>
              <p className="mt-4 text-bodytext/80">{t("ow.communityIntro")}</p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {beyondPrograms.map((b) => (
                  <Link
                    key={b.id}
                    to={`/our-work/${b.id}`}
                    className="card-ivory p-5 flex items-center gap-4 no-underline text-bodytext"
                    data-testid={`community-service-${b.id}`}
                  >
                    <b.icon size={24} strokeWidth={1.5} className="text-saffron shrink-0" aria-hidden="true" />
                    <span className="font-sans font-semibold text-[15px]">{kn ? b.knLabel : b.label}</span>
                  </Link>
                ))}
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="img-zoom !rounded-2xl aspect-[4/3]">
                <img src="/assets/gallery/g6.webp" alt="Lamp-lighting ceremony with IBMCT monastics, dignitaries and community members" className="w-full h-full object-cover" loading="lazy" data-testid="community-services-image" />
              </div>
            </Reveal>
          </div>
          <Reveal className="mt-12 flex flex-wrap gap-4 items-center">
            <Users size={22} className="text-saffron" aria-hidden="true" />
            <OutlineLink to="/volunteer" testId="our-work-volunteer-button">{t("ow.volunteer")}</OutlineLink>
            <PrimaryLink to="/donate" testId="our-work-donate-button">{t("ow.donate")}</PrimaryLink>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default OurWork;
